import { useState } from 'react';
import type { IssueVoucher } from '@salonomy/types';
import { Button, Field, Modal, Pill, SegmentedControl, TextArea, TextInput } from '@salonomy/ui';
import { useIssueVoucher } from '../../data/vouchers.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { poundsToPence } from './money.js';

/** What the till sells/issues a gift card as. SALE = money in; COMP = gifted balance, no money in. */
type Tender = IssueVoucher['tender'];

/**
 * WS9 §2 — issue / sell a GIFT CARD at the till (surface c). Multi-purpose ONLY: a general salon gift
 * card is redeemable across services AND retail at differing VAT rates, so it's an MPV by law (ADR-0008).
 * Single-purpose vouchers are deferred server-side (issue rejects `purpose=single` with 422), so this
 * form does NOT offer a single-purpose choice — there's nothing here that would 422.
 *
 * ACCOUNTING (the point — Overview §3.5 / ADR-0009): a sale is a LIABILITY, not revenue. The till takes
 * the money and the API credits the gift-card liability (and writes a `vatExempt` voucher_sale receipt
 * line — £0 VAT, £0 revenue at sale); revenue/VAT land only at redemption. So the sale appears on the
 * receipt exactly once and can never be counted twice. A COMP posts to comp_expense (the salon's
 * marketing spend funded the balance) and requires a reason.
 *
 * Issue is idempotent on a per-intent key minted once on submit; the button is disabled while pending so
 * a double-tap can't issue two cards, and a 422 surfaces faithfully.
 */
export function IssueGiftCardModal({
  clientId,
  onIssued,
  onClose,
}: {
  clientId: string | null;
  /** Called with the issued amount (pence) + tender so the till can show the receipt line it produced. */
  onIssued: (amountPence: number, tender: Tender) => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const issue = useIssueVoucher();

  const [amount, setAmount] = useState('');
  const [tender, setTender] = useState<Tender>('card');
  const [reason, setReason] = useState('');

  const amountPence = poundsToPence(amount);
  const isComp = tender === 'comp';
  const reasonRequired = isComp; // a comp is a gifted balance — it carries an audit reason (ADR-0009)
  const canIssue =
    amountPence != null && amountPence > 0 && (!reasonRequired || reason.trim().length > 0);

  async function submit() {
    if (!canIssue || amountPence == null) return;
    try {
      // Mint the idempotency key once per intent; the button is disabled while pending so a double-tap
      // can't issue a second card, and the API is idempotent on this key.
      const voucher = await issue.mutateAsync({
        kind: 'gift_card',
        purpose: 'multi', // multi-purpose only — single is deferred server-side (422)
        amountPence,
        tender,
        clientId: clientId ?? undefined,
        reason: reason.trim() ? reason.trim() : undefined,
        idempotencyKey: crypto.randomUUID(),
      });
      onIssued(amountPence, tender);
      toast.success(
        isComp
          ? `Comped ${money(amountPence)} gift card · ${voucher.code}`
          : `Sold ${money(amountPence)} gift card · ${voucher.code}`,
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not issue the gift card');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Issue gift card"
      width={440}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canIssue || issue.isPending}>
            {issue.isPending ? 'Issuing…' : isComp ? 'Comp gift card' : 'Sell gift card'}
          </Button>
        </>
      }
    >
      <Field label="Amount (£)" hint="The face value loaded onto the card.">
        <TextInput
          autoFocus
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="50.00"
        />
      </Field>

      <Field label="Paid by">
        <SegmentedControl<Tender>
          options={[
            { value: 'card', label: 'Card' },
            { value: 'cash', label: 'Cash' },
            { value: 'comp', label: 'Comp' },
          ]}
          value={tender}
          onChange={setTender}
        />
      </Field>

      {reasonRequired ? (
        <Field label="Comp reason" hint="A comped balance is recorded against you with a reason.">
          <TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this card is being comped"
            rows={2}
          />
        </Field>
      ) : null}

      <div style={{ marginTop: 4 }}>
        <Pill tone="neutral">Multi-purpose gift card</Pill>
      </div>
      <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 8 }}>
        {isComp
          ? 'A comped card is the salon’s own marketing spend — it’s recorded as a liability the salon will honour, with no revenue or VAT at issue.'
          : 'A gift-card sale is held as a liability, not revenue — the takings recognise it only when it’s redeemed, so it’s never counted twice. No VAT at sale.'}
      </div>
    </Modal>
  );
}
